/** @jsxImportSource @emotion/react */
import { useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { Button, Modal } from 'react-bootstrap'
import Select from 'react-select'

import {
  useAddCompanyRegionMutation,
  useUpdateCompanyRegionMutation,
  useDeleteCompanyRegionMutation,
} from 'store/services/office'
import Input from 'components/atoms/Input'
import { KeyPerson } from 'ts-back-end/src/business/company/dto/company.dto'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const RegionItem = styled.div<{ depth: number }>`
  margin-left: ${(props) => props.depth * 24}px;
  border-left: ${(props) => (props.depth > 0 ? '2px solid #dee2e6' : 'none')};
  padding-left: ${(props) => (props.depth > 0 ? '12px' : '0')};
`

const RegionCard = styled.div`
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 8px;
`

const RegionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const RegionName = styled.span`
  font-weight: 500;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`

const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: #6c757d;
  font-size: 12px;

  &:hover {
    color: #495057;
  }
`

const ManagerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  font-size: 14px;
`

const ManagerLabel = styled.span`
  color: #6d757e;
  min-width: 60px;
`

const AddSubRegionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`

const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 38,
    height: 38,
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: 38,
  }),
}

const AddRegionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`

// Region tree node type
interface RegionTreeNode {
  uuid: string
  regionName: string
  parentUuid?: string | null
  managerKeyPersonIndex?: number | null
  children?: RegionTreeNode[]
}

interface IProps {
  regions: RegionTreeNode[]
  companyId: string
  disabled?: boolean
  keyPeople?: KeyPerson[] | null
}

// Helper to count all descendants
const countDescendants = (node: RegionTreeNode): number => {
  if (!node.children?.length) return 0
  return node.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0)
}

// Helper to get all descendant names
const getDescendantNames = (node: RegionTreeNode): string[] => {
  if (!node.children?.length) return []
  const names: string[] = []
  for (const child of node.children) {
    names.push(child.regionName)
    names.push(...getDescendantNames(child))
  }
  return names
}

// Single region node component
const RegionNode = ({
  region,
  depth,
  companyId,
  disabled,
  keyPeople,
  expandedRegions,
  toggleExpand,
  editRegion,
  setEditRegion,
  editEntry,
  setEditEntry,
  editManager,
  setEditManager,
  addSubRegionParent,
  setAddSubRegionParent,
  newSubEntry,
  setNewSubEntry,
  newSubManager,
  setNewSubManager,
  onDelete,
}: {
  region: RegionTreeNode
  depth: number
  companyId: string
  disabled?: boolean
  keyPeople?: KeyPerson[] | null
  expandedRegions: Set<string>
  toggleExpand: (uuid: string) => void
  editRegion: string | null
  setEditRegion: (uuid: string | null) => void
  editEntry: string
  setEditEntry: (val: string) => void
  editManager: number | null
  setEditManager: (val: number | null) => void
  addSubRegionParent: string | null
  setAddSubRegionParent: (uuid: string | null) => void
  newSubEntry: string
  setNewSubEntry: (val: string) => void
  newSubManager: number | null
  setNewSubManager: (val: number | null) => void
  onDelete: (region: RegionTreeNode) => void
}) => {
  const [updateRegion] = useUpdateCompanyRegionMutation()
  const [addRegion] = useAddCompanyRegionMutation()

  const hasChildren = region.children && region.children.length > 0
  const isExpanded = expandedRegions.has(region.uuid)
  const isEditing = editRegion === region.uuid
  const isAddingSubRegion = addSubRegionParent === region.uuid

  const keyPeopleOptions = useMemo(
    () =>
      keyPeople?.map((person, index) => ({
        label: `${person.fullName}${person.title ? ` (${person.title})` : ''}`,
        value: index,
      })) || [],
    [keyPeople],
  )

  const currentManager =
    region.managerKeyPersonIndex != null && keyPeople?.[region.managerKeyPersonIndex]
      ? keyPeople[region.managerKeyPersonIndex]
      : null

  return (
    <RegionItem depth={depth}>
      <RegionCard>
        <RegionHeader>
          {!isEditing ? (
            <>
              <RegionName>
                {hasChildren && (
                  <ExpandButton onClick={() => toggleExpand(region.uuid)}>
                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                  </ExpandButton>
                )}
                {!hasChildren && <span style={{ width: 28 }} />}
                {region.regionName}
                {hasChildren && (
                  <span style={{ color: '#6c757d', fontSize: '12px' }}>
                    ({region.children?.length} sub-region{region.children?.length !== 1 ? 's' : ''})
                  </span>
                )}
              </RegionName>
              <ActionButtons>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setAddSubRegionParent(region.uuid)
                    setNewSubEntry('')
                    setNewSubManager(null)
                  }}
                  disabled={disabled}>
                  + Sub-Region
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setEditRegion(region.uuid)
                    setEditEntry(region.regionName)
                    setEditManager(region.managerKeyPersonIndex ?? null)
                  }}
                  disabled={disabled}>
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onDelete(region)}
                  disabled={disabled}>
                  Remove
                </Button>
              </ActionButtons>
            </>
          ) : (
            <>
              <Input
                parentDiv={null}
                name="editEntry"
                className="form-control"
                css={{ flex: 1 }}
                placeholder="Region Name"
                value={editEntry}
                handleChange={(e) => setEditEntry(e.target.value)}
              />
              <ActionButtons>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() =>
                    updateRegion({
                      id: Number(companyId),
                      data: {
                        uuid: region.uuid,
                        regionName: editEntry,
                        managerKeyPersonIndex: editManager,
                      },
                    })
                      .unwrap()
                      .then(() => {
                        setEditRegion(null)
                        setEditEntry('')
                        setEditManager(null)
                      })
                  }
                  disabled={!editEntry}>
                  Save
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setEditRegion(null)
                    setEditEntry('')
                    setEditManager(null)
                  }}>
                  Cancel
                </Button>
              </ActionButtons>
            </>
          )}
        </RegionHeader>

        {/* Manager row - shown when not editing */}
        {!isEditing && (
          <ManagerRow>
            <ManagerLabel>Manager:</ManagerLabel>
            {currentManager ? (
              <span>
                {currentManager.fullName}
                {currentManager.title ? ` (${currentManager.title})` : ''}
              </span>
            ) : (
              <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>None assigned</span>
            )}
          </ManagerRow>
        )}

        {/* Manager selection when editing */}
        {isEditing && (
          <ManagerRow>
            <ManagerLabel>Manager:</ManagerLabel>
            <div css={{ flex: 1 }}>
              <Select
                isClearable
                placeholder="Select manager..."
                options={keyPeopleOptions}
                value={keyPeopleOptions.find((opt) => opt.value === editManager) || null}
                onChange={(selected) => setEditManager(selected?.value ?? null)}
                styles={selectStyles}
              />
            </div>
          </ManagerRow>
        )}

        {/* Add sub-region form */}
        {isAddingSubRegion && (
          <AddSubRegionRow>
            <Input
              parentDiv={null}
              name="newSubEntry"
              className="form-control"
              css={{ flex: 1 }}
              placeholder="New Sub-Region Name"
              value={newSubEntry}
              handleChange={(e) => setNewSubEntry(e.target.value)}
            />
            <div css={{ minWidth: 200 }}>
              <Select
                isClearable
                placeholder="Manager..."
                options={keyPeopleOptions}
                value={keyPeopleOptions.find((opt) => opt.value === newSubManager) || null}
                onChange={(selected) => setNewSubManager(selected?.value ?? null)}
                styles={selectStyles}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={!newSubEntry}
              onClick={async () => {
                await addRegion({
                  id: Number(companyId),
                  regionName: newSubEntry,
                  parentUuid: region.uuid,
                  managerKeyPersonIndex: newSubManager,
                }).unwrap()
                setAddSubRegionParent(null)
                setNewSubEntry('')
                setNewSubManager(null)
                // Auto-expand parent to show new child
                if (!expandedRegions.has(region.uuid)) {
                  toggleExpand(region.uuid)
                }
              }}>
              Add
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setAddSubRegionParent(null)
                setNewSubEntry('')
                setNewSubManager(null)
              }}>
              Cancel
            </Button>
          </AddSubRegionRow>
        )}
      </RegionCard>

      {/* Render children if expanded */}
      {hasChildren && isExpanded && (
        <div>
          {region.children?.map((child) => (
            <RegionNode
              key={child.uuid}
              region={child}
              depth={depth + 1}
              companyId={companyId}
              disabled={disabled}
              keyPeople={keyPeople}
              expandedRegions={expandedRegions}
              toggleExpand={toggleExpand}
              editRegion={editRegion}
              setEditRegion={setEditRegion}
              editEntry={editEntry}
              setEditEntry={setEditEntry}
              editManager={editManager}
              setEditManager={setEditManager}
              addSubRegionParent={addSubRegionParent}
              setAddSubRegionParent={setAddSubRegionParent}
              newSubEntry={newSubEntry}
              setNewSubEntry={setNewSubEntry}
              newSubManager={newSubManager}
              setNewSubManager={setNewSubManager}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </RegionItem>
  )
}

export const Regions = ({ regions, disabled, companyId, keyPeople }: IProps) => {
  const [newEntry, setNewEntry] = useState('')
  const [newManager, setNewManager] = useState<number | null>(null)
  const [editEntry, setEditEntry] = useState('')
  const [editManager, setEditManager] = useState<number | null>(null)
  const [editRegion, setEditRegion] = useState<string | null>(null)
  const [addSubRegionParent, setAddSubRegionParent] = useState<string | null>(null)
  const [newSubEntry, setNewSubEntry] = useState('')
  const [newSubManager, setNewSubManager] = useState<number | null>(null)
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set())
  const [deleteModal, setDeleteModal] = useState<RegionTreeNode | null>(null)

  const [addRegion] = useAddCompanyRegionMutation()
  const [deleteRegion] = useDeleteCompanyRegionMutation()

  const toggleExpand = (uuid: string) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) {
        next.delete(uuid)
      } else {
        next.add(uuid)
      }
      return next
    })
  }

  const keyPeopleOptions = useMemo(
    () =>
      keyPeople?.map((person, index) => ({
        label: `${person.fullName}${person.title ? ` (${person.title})` : ''}`,
        value: index,
      })) || [],
    [keyPeople],
  )

  const handleDelete = (region: RegionTreeNode) => {
    const descendantCount = countDescendants(region)
    if (descendantCount > 0) {
      // Show warning modal
      setDeleteModal(region)
    } else {
      // Direct delete
      deleteRegion({ id: Number(companyId), uuid: region.uuid })
    }
  }

  const confirmDelete = () => {
    if (deleteModal) {
      deleteRegion({ id: Number(companyId), uuid: deleteModal.uuid })
      setDeleteModal(null)
    }
  }

  // Filter to only top-level regions (those without parentUuid or with null parentUuid)
  const topLevelRegions = useMemo(
    () => regions.filter((r) => !r.parentUuid),
    [regions],
  )

  return (
    <Container>
      {/* Add new top-level region */}
      <span style={{ color: '#6D757E' }} className="mb-1">
        Add Region
      </span>
      <AddRegionRow>
        <Input
          parentDiv={null}
          name="newEntry"
          disabled={disabled}
          className="form-control"
          placeholder="Region Name"
          value={newEntry}
          handleChange={(e) => setNewEntry(e.target.value)}
        />
        <div css={{ minWidth: 220 }}>
          <Select
            isClearable
            isDisabled={disabled}
            placeholder="Manager..."
            options={keyPeopleOptions}
            value={keyPeopleOptions.find((opt) => opt.value === newManager) || null}
            onChange={(selected) => setNewManager(selected?.value ?? null)}
            styles={selectStyles}
          />
        </div>
        <Button
          disabled={!newEntry || disabled}
          variant="primary"
          style={{ minWidth: 100 }}
          onClick={async () => {
            if (newEntry) {
              await addRegion({
                id: Number(companyId),
                regionName: newEntry,
                managerKeyPersonIndex: newManager,
              }).unwrap()
              setNewEntry('')
              setNewManager(null)
            }
          }}>
          Add Region
        </Button>
      </AddRegionRow>

      {/* Regions list */}
      {Array.isArray(regions) && (
        <>
          <div
            className="row flex-row flex-nowrap justify-content-between align-items-center p-2 my-2"
            style={{
              color: '#6D757E',
              borderBottom: '1px solid #ced4da',
            }}>
            <div style={{ width: '100%' }}>Regions & Sub-Regions</div>
          </div>

          {!topLevelRegions?.length && (
            <div className="row my-1">
              <div className="col-sm-12 p-0">
                <div className="list-group text-secondary">
                  <div className="list-group-item list-group-item-action flex-column align-items-center">
                    <div className="d-flex py-1 justify-content-center align-items-center">
                      <span>No Regions Entered</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {topLevelRegions.map((region) => (
            <RegionNode
              key={region.uuid}
              region={region}
              depth={0}
              companyId={companyId}
              disabled={disabled}
              keyPeople={keyPeople}
              expandedRegions={expandedRegions}
              toggleExpand={toggleExpand}
              editRegion={editRegion}
              setEditRegion={setEditRegion}
              editEntry={editEntry}
              setEditEntry={setEditEntry}
              editManager={editManager}
              setEditManager={setEditManager}
              addSubRegionParent={addSubRegionParent}
              setAddSubRegionParent={setAddSubRegionParent}
              newSubEntry={newSubEntry}
              setNewSubEntry={setNewSubEntry}
              newSubManager={newSubManager}
              setNewSubManager={setNewSubManager}
              onDelete={handleDelete}
            />
          ))}
        </>
      )}

      {/* Delete confirmation modal */}
      <Modal show={!!deleteModal} onHide={() => setDeleteModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning mr-2" />
            Delete Region?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteModal && (
            <>
              <p>
                <strong>"{deleteModal.regionName}"</strong> has{' '}
                {countDescendants(deleteModal)} sub-region
                {countDescendants(deleteModal) !== 1 ? 's' : ''} that will also be deleted:
              </p>
              <ul style={{ maxHeight: 200, overflowY: 'auto' }}>
                {getDescendantNames(deleteModal).map((name, idx) => (
                  <li key={idx}>{name}</li>
                ))}
              </ul>
              <p className="text-danger mb-0">
                <strong>This action cannot be undone.</strong>
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModal(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete All
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
